import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import type { FeatureFlag } from "./types";
import { FeatureFlagTypeBadge } from "./FeatureFlagTypeBadge";
import { FeatureFlagVisibilityBadge } from "./FeatureFlagVisibilityBadge";
import { formatValueInline } from "./format-value";

interface FeatureFlagRowProps {
  readonly flag: FeatureFlag;
}

export function FeatureFlagRow({ flag }: FeatureFlagRowProps) {
  const { t } = useTranslation();
  const inlineValue = formatValueInline(flag.value, flag.valueType);

  return (
    <Link
      to={`/feature-flags/${encodeURIComponent(flag.id)}`}
      data-testid={`feature-flag-row-${flag.id}`}
      className="group block rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:border-wander-orange/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      aria-label={t("featureFlags.row.openLabel", { id: flag.id })}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <span className="break-all font-mono text-sm font-semibold text-foreground">
            {flag.id}
          </span>
          <FeatureFlagTypeBadge valueType={flag.valueType} />
          <FeatureFlagVisibilityBadge isPublic={flag.isPublic} />
        </div>
        <ArrowUpRight
          className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-wander-orange"
          aria-hidden="true"
        />
      </div>

      <p className="mt-2 line-clamp-2 font-sans text-sm font-normal text-muted-foreground">
        {flag.description}
      </p>

      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-sans text-xs text-muted-foreground">
        <div className="flex min-w-0 items-center gap-1.5">
          <dt className="font-medium text-foreground">{t("featureFlags.row.value")}:</dt>
          <dd
            data-testid={`feature-flag-row-${flag.id}-value`}
            className="truncate font-mono text-xs text-foreground"
          >
            {inlineValue}
          </dd>
        </div>
      </dl>
    </Link>
  );
}
